import React, { useEffect, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import API from "@/lib/api";
import MapView from "@/components/MapView";
import { unslug } from "@/lib/slug";
import { useApp } from "@/context/AppContext";
import { Star, MapPin, Phone, Globe, BadgeCheck, List, Map as MapIcon } from "lucide-react";

export default function PublicDirectory() {
  const { catSlug, citySlug } = useParams();
  const navigate = useNavigate();
  const { getCategory, catName } = useApp();
  const [items, setItems] = useState(null);
  const [view, setView] = useState("list");
  const cat = getCategory(catSlug);
  const city = unslug(citySlug);
  const catLabel = cat ? catName(cat) : unslug(catSlug);

  useEffect(() => {
    API.get(`/public/businesses?category=${catSlug}&city=${encodeURIComponent(city)}`)
      .then((r) => setItems(r.data)).catch(() => setItems([]));
  }, [catSlug, citySlug]);

  useEffect(() => {
    const title = `${catLabel} a ${city} | FYDATE`;
    document.title = title;
    let meta = document.querySelector('meta[name="description"]');
    if (!meta) { meta = document.createElement("meta"); meta.name = "description"; document.head.appendChild(meta); }
    meta.content = `Trova ${catLabel.toLowerCase()} a ${city}: attività verificate, recensioni e contatti. FYDATE, la bacheca dei bisogni.`;
    let ld = document.getElementById("fidate-jsonld");
    if (!ld) { ld = document.createElement("script"); ld.type = "application/ld+json"; ld.id = "fidate-jsonld"; document.head.appendChild(ld); }
    ld.text = JSON.stringify({
      "@context": "https://schema.org", "@type": "ItemList",
      "name": title,
      "itemListElement": (items || []).map((b, i) => ({
        "@type": "ListItem", "position": i + 1,
        "item": { "@type": "LocalBusiness", "name": b.trade_name,
          "address": { "@type": "PostalAddress", "addressLocality": b.city },
          "telephone": b.public_contacts?.phone || undefined,
          "aggregateRating": b.avg_rating ? { "@type": "AggregateRating", "ratingValue": b.avg_rating, "reviewCount": b.review_count } : undefined } })),
    });
    return () => { document.title = "FYDATE"; };
  }, [items, catLabel, city]);

  return (
    <div className="min-h-screen bg-[#EEF1F7]">
      <header className="bg-[#EEF1F7]/95 backdrop-blur-md border-b border-[#DADFEA] sticky top-0 z-10">
        <div className="max-w-4xl mx-auto px-4 h-14 flex items-center justify-between">
          <Link to="/" className="font-serif text-2xl font-bold text-[#141A3A]">FYDATE</Link>
          <button onClick={() => navigate("/search")} className="text-sm text-[#141A3A] font-medium">{"Apri l'app"}</button>
        </div>
      </header>
      <main className="max-w-4xl mx-auto px-4 py-6">
        <h1 className="font-serif text-3xl font-bold text-[#0F1230] mb-1">{catLabel} a {city}</h1>
        <p className="text-[#4A4E68] mb-4">Attività della categoria {catLabel.toLowerCase()} nella zona di {city}, con recensioni e contatti su FYDATE.</p>

        <div className="flex bg-[#EDEDF4] rounded-full p-1 w-fit mb-5">
          <button data-testid="public-view-list" onClick={() => setView("list")} className={`inline-flex items-center gap-1 px-3 h-9 rounded-full text-sm ${view === "list" ? "bg-white text-[#141A3A] font-medium" : "text-[#4A4E68]"}`}><List size={15} /> Elenco</button>
          <button data-testid="public-view-map" onClick={() => setView("map")} className={`inline-flex items-center gap-1 px-3 h-9 rounded-full text-sm ${view === "map" ? "bg-white text-[#141A3A] font-medium" : "text-[#4A4E68]"}`}><MapIcon size={15} /> Mappa</button>
        </div>

        {items === null ? <p className="text-[#4A4E68]">Caricamento...</p> : view === "map" ? (
          <MapView items={items} onSelect={(b) => navigate(`/business/${b.id}`)} />
        ) : items.length === 0 ? (
          <p className="text-[#4A4E68]">Nessuna attività trovata per {catLabel} a {city}.</p>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            {items.map((b) => (
              <div key={b.id} className="bg-white rounded-[18px] border border-[#DADFEA] p-4">
                <div className="flex items-start justify-between gap-2">
                  <h2 className="font-semibold text-[#0F1230]">{b.trade_name}</h2>
                  {b.avg_rating != null && <span className="text-sm text-[#4A4E68] inline-flex items-center gap-0.5"><Star size={14} className="fill-[#E8590C] text-[#E8590C]" /> {b.avg_rating}</span>}
                </div>
                <p className="text-sm text-[#4A4E68] inline-flex items-center gap-1 mt-0.5"><MapPin size={13} /> {b.city}</p>
                {b.admin_verified && <span className="inline-flex items-center gap-1 rounded-full bg-[#E8F5E9] text-[#2E7D32] px-2 py-0.5 text-xs font-medium ml-2"><BadgeCheck size={13} /> Verificata</span>}
                <div className="flex gap-2 mt-3">
                  {b.public_contacts?.phone && <a href={`tel:${b.public_contacts.phone}`} className="inline-flex items-center gap-1 text-sm border border-[#DADFEA] rounded-full px-3 h-9"><Phone size={14} /> Chiama</a>}
                  {b.public_contacts?.website && <a href={b.public_contacts.website} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 text-sm border border-[#DADFEA] rounded-full px-3 h-9"><Globe size={14} /> Sito</a>}
                </div>
              </div>
            ))}
          </div>
        )}

        <div className="text-center mt-10 text-sm text-[#4A4E68]">
          <span className="font-serif font-semibold text-[#141A3A]">FYDATE</span> — La bacheca dei bisogni
        </div>
      </main>
    </div>
  );
}
